import { WidgetCategory, WidgetSubCategory, Widget } from './state';
import { HELP_ORIGIN } from '../../constants';

// these constants are still needed because not everything is in Typescript yet
export const UPDATE_WIDGET_TOOLBOX_WIDGETS = 'UPDATE_WIDGET_TOOLBOX_WIDGETS';
export const WIDGET_CATEGORY_CORE = 'WIDGET_CATEGORY_CORE';
export const WIDGET_CATEGORY_QUIZ = 'WIDGET_CATEGORY_QUIZ';
export const WIDGET_CATEGORY_INTEGRATION = 'WIDGET_CATEGORY_INTEGRATION';
export const WIDGET_CATEGORY_SUPERUSER = 'WIDGET_CATEGORY_SUPERUSER';
export const WIDGET_CATEGORY_ADVANCED = 'WIDGET_CATEGORY_ADVANCED';
export const WIDGET_CATEGORY_LTI = 'WIDGET_CATEGORY_LTI';
export const WIDGET_CATEGORY_BETA = 'WIDGET_CATEGORY_BETA';

export interface WidgetWithIndex extends Widget {
  // Need this for <ConnectedWidgetIcon>, so drag and drop would work properly
  index: number;
}

export interface WidgetToolboxCategoriesMap {
  [categoryId: string]: WidgetWithIndex[];
}

interface WidgetToolboxSubCategoryBase {
  subCategoryId: WidgetSubCategory;
  name: string;
  helpAction?: string;
}
interface WidgetToolboxSubCategoryHard extends WidgetToolboxSubCategoryBase {
  includesWidgetIdentifiers: string[];
}
interface WidgetToolboxSubCategorySoft extends WidgetToolboxSubCategoryBase {
  includesWidgetsWithCategory: string;
}
interface WidgetToolboxSubCategoryCustom extends WidgetToolboxSubCategoryBase {
  custom: 'showOther';
}

export interface WidgetToolboxCategory {
  categoryId: WidgetCategory;
  name: string;
  subCategories: Array<
    | WidgetToolboxSubCategoryHard
    | WidgetToolboxSubCategorySoft
    | WidgetToolboxSubCategoryCustom
  >;
}

// this is the skeleton structure for the categories and sub categories in widget toolbox
// TODO: maybe this should be stored in the redux store rather than passing
// into the selectors directly?
export const WIDGET_TOOLBOX_STRUCTURE: WidgetToolboxCategory[] = [
  {
    categoryId: 'WIDGET_CATEGORY_CORE',
    name: 'Core',
    subCategories: [
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_CORE_CONTENT',
        name: 'Content',
        helpAction: `${HELP_ORIGIN}/t/634qpq`,
        includesWidgetIdentifiers: [
          'native/OpenLearning/RichTextWidget',
          'native/OpenLearning/WhiteSpaceWidget',
          'native/OpenLearning/VideoWidget',
          'native/OpenLearning/AccessibleVideoWidget',
          'native/OpenLearning/FileWidget',
          'ol/OpenLearning/play-audio-file',
        ],
      },
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_CORE_POSTING',
        name: 'Posting',
        helpAction: `${HELP_ORIGIN}/t/184qr4`,
        includesWidgetIdentifiers: [
          'native/OpenLearning/ShareTextWidget',
          'native/OpenLearning/ShareImageWidget',
          'native/OpenLearning/ShareFileWidget',
        ],
      },
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_CORE_INTERACTIVE',
        name: 'Interactive prompts',
        helpAction: `${HELP_ORIGIN}/t/k94h2c`,
        includesWidgetIdentifiers: [
          'ol/OpenLearning/checklist',
          'ol/OpenLearning/random-selection',
        ],
      },
    ],
  },
  {
    categoryId: 'WIDGET_CATEGORY_QUIZ',
    name: 'Quiz',
    subCategories: [
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_QUIZ_QUESTIONS',
        name: 'Questions',
        helpAction: `${HELP_ORIGIN}/t/y74hwb`,
        includesWidgetIdentifiers: [
          'ol/OpenLearning/multiple-choice',
          'ol/OpenLearning/short-answer',
          'ol/OpenLearning/fill-in-the-blanks',
          'ol/OpenLearning/line-matching',
          'ol/OpenLearning/category-matching',
          'ol/OpenLearning/crossword',
          'native/OpenLearning/ExampleQuizWidget',
        ],
      },
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_QUIZ_SUBMIT',
        name: 'Submit',
        helpAction: `${HELP_ORIGIN}/t/y74hwb#submitactions`,
        includesWidgetIdentifiers: ['ol/OpenLearning/submit-button'],
      },
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_QUIZ_CONTENT',
        name: 'Content',
        helpAction: `${HELP_ORIGIN}/t/y74hwb#contentwidgets`,
        includesWidgetIdentifiers: [
          'native/OpenLearning/RichTextWidget',
          'native/OpenLearning/WhiteSpaceWidget',
          'native/OpenLearning/VideoWidget',
          'native/OpenLearning/FileWidget',
          'ol/OpenLearning/play-audio-file',
        ],
      },
    ],
  },
  {
    categoryId: 'WIDGET_CATEGORY_BETA',
    name: 'Beta',
    subCategories: [
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_SOCIAL',
        name: 'Social tools',
        helpAction: `${HELP_ORIGIN}/t/y4h6bcs`,
        includesWidgetsWithCategory: 'beta',
      },
    ],
  },
  {
    categoryId: 'WIDGET_CATEGORY_INTEGRATION',
    name: 'Integration',
    subCategories: [
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_INTEGRATION_LTI',
        name: 'LTI tools',
        helpAction: `${HELP_ORIGIN}/t/639gh6#lti-integrations`,
        includesWidgetsWithCategory: 'ltiprovider',
      },
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_INTEGRATION_EXTENSIONS',
        name: 'Embed tools',
        helpAction: `${HELP_ORIGIN}/t/639gh6#other-integrations`,
        includesWidgetIdentifiers: [
          'ol/OpenLearning/html',
          'ol/OpenLearning/upload-html',
          'ol/OpenLearning/upload-zip',
          'ol/OpenLearning/iframe',
          'native/OpenLearning/EmbedWidget',
        ],
      },
    ],
  },
  {
    categoryId: 'WIDGET_CATEGORY_ADVANCED',
    name: 'Advanced',
    subCategories: [
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_ADVANCED_COLLECTIONS',
        name: 'Collections',
        helpAction: `${HELP_ORIGIN}/t/184qr4`,
        includesWidgetIdentifiers: [
          'native/OpenLearning/GalleryWidget',
          'native/OpenLearning/PreviewFeedGalleryWidget',
        ],
      },
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_ADVANCED_OTHER',
        name: 'Miscellaneous',
        custom: 'showOther',
      },
    ],
  },
  {
    categoryId: 'WIDGET_CATEGORY_SUPERUSER',
    name: 'Superuser only',
    subCategories: [
      {
        subCategoryId: 'WIDGET_SUB_CATEGORY_SUPERUSER_SUPERUSER',
        name: 'Superuser only',
        includesWidgetIdentifiers: [
          'ol/OpenLearning/bigbluebutton',
          'native/OpenLearning/PreviewFeedGalleryWidget',
          'native/OpenLearning/ExamWidget',
        ],
      },
    ],
  },
];
